import { useState, createContext, useContext, useEffect, useRef } from "react";
import { decryptText } from "../Utils/OstreonEncryption";
import config from "../config";
import { useNavigate } from "react-router-dom";
import Loading from "../Components/Loading/Loading";
import { decryptData, encryptData } from "../Utils/dataEncryption";
import { useAuth } from "./AuthContext";
import { fetchStripeProducts } from "../Utils/StripeUtils";
import { handleTips, isUserActive } from "../Utils/handleIssues";
import { ensurePartnerKeys, publishKeyToPartner } from "../Utils/partnerKeys";
import { ensureRecoveryKey } from "../Utils/recoveryKey";
import { stampUpdatedAssets } from "../Utils/assetLastUpdated";
import { LanguageContext } from "./changeLanguageProvider";
import { updateCustomerLanguage } from "../Utils/translationUtils";
import { sendUIErrorToBackend } from "../Utils/sendErrorToBackend";

const base_url = config.base_url;

/*
  Exported so a view can render the app's own pages against data other than the
  signed-in user's -- the partner's read-only view of one customer wraps them in
  a provider of its own. Everything else should keep using the UserData() hook.
*/
export const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const private_key = window.sessionStorage.getItem("ostreon_key");
  const isUserPage =
    window.location.pathname.startsWith("/user") ||
    window.location.pathname.startsWith("/partner");
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [dublicatedcontent, setDublicatedContent] = useState(
    websiteCurrentLang.content.DublicatedText
  );
  useEffect(() => {
    setDublicatedContent(websiteCurrentLang.content.DublicatedText);
  }, [websiteCurrentLang]);
  const [products, setProducts] = useState();
  const [currency, setCurrency] = useState();
  const [error, setError] = useState(null);
  const [isFreezed, setFreezed] = useState(false);
  const [MyAccount, setMyAccount] = useState();
  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [BillingData, setBillingData] = useState();
  const [UserData, setUserData] = useState();
  const [CustomerUser, setCustomerUser] = useState();
  const [CustomerBeneficiary, setCustomerBeneficiary] = useState();
  const [upda, Update] = useState();
  const [referralData, setReferralData] = useState();
  const [userValidationData, setUserValidationData] = useState();
  /*
    The customer's partner, or null. Read by the Partner Sharing section of the
    asset modals so it can name who the asset would be shared with -- and hide
    itself when there is nobody.

    Deliberately not part of the loading gate below: most users have no partner,
    and the app must not wait on a request whose usual answer is "none".
  */
  const [myPartner, setMyPartner] = useState(null);
  const [promotionData, setPromotionData] = useState();
  const [blink, setBlink] = useState(false);
  const navigate = useNavigate();
  const pushBlink = (time = 2000) => {
    if (blink) return;
    setBlink(true);
    setTimeout(() => {
      setBlink(false);
    }, time);
  };
  const edit = (field, value) => {
    setUserData({
      ...UserData,
      [field]: value,
    });
  };
  useEffect(() => {
    updateCustomerLanguage(websiteCurrentLang?.label);
  }, [websiteCurrentLang?.label]);

  const getUserData = () => {
    fetch(base_url + "user/data", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 403) navigate("/logout", { replace: true });
        return res.json();
      })
      .then((data) => {
        if (!private_key) {
          sendUIErrorToBackend("No private key");
          window.location.assign(
            `${window?.location?.protocol}//${window?.location?.host}/login`
          );
        } else {
          decryptData(data, private_key)
            .then((data) => {
              console.log("Decrypted UserData");
              console.log(data);
              const name = data.customer_full_name.split(" ");
              const customer_name = name[0];
              const customer_last_name =
                name.length === 1 ? undefined : name[name.length - 1];
              setUserData({
                ...data,
                customer_name,
                customer_last_name,
              });
            })
            .catch((e) => {
              console.log(e);
              sendUIErrorToBackend(e);
              setError("An error occurred while decrypting data");
            });
        }
      })
      .catch((e) => {
        console.log(e);
        setError("An error occurred while loading data");
      });
  };
  /*
    The assets as they were the last time this browser and the server agreed:
    set when the account loads, and again on every save. It is what the "last
    update" stamp is measured against -- see Utils/assetLastUpdated.js.

    Null until the account has loaded. That is the difference between "no
    asset has changed" and "we do not know yet", and it is what keeps the
    first load from stamping today's date onto every asset on the account.
  */
  const lastSyncedAssets = useRef(null);

  const UserDataUpdate = async () => {
    if (!UserData?.user_id) return;
    if (!private_key) {
      sendUIErrorToBackend("No private key");
      window.location.assign(
        `${window?.location?.protocol}//${window?.location?.host}/login`
      );
    } else {
      const new_data = await encryptData(UserData, private_key);
      console.log(new_data);
      fetch(base_url + "user/data/update", {
        method: "POST",
        mode: "cors",
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
          credentials: "include",
        },
        body: JSON.stringify(new_data),
        credentials: "include",
      })
        .then((res) => {
          return res.json();
        })
        .then(async (data) => {
          return data;
        })
        .catch((e) => console.log(e));
    }
  };
  const getCustomerUser = () => {
    fetch(base_url + "user/customer", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 403) navigate("/logout");
        else if (res.status === 200) return res.json();
        else throw new Error();
      })
      .then((data) => {
        data.user_status === "freezed" ? setFreezed(true) : setFreezed(false);
        if (UserData && data?.user_status !== "freezed") {
          if (isUserActive(UserData)) data.user_status = "active";
          else data.user_status = "inactive";
        }
        setCustomerUser(data);
      })
      .catch((e) => {
        console.log(e);
        navigate("/logout", { replace: true });
      });
  };
  const getBillingData = () => {
    fetch(base_url + "user/billing", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 403) navigate("/logout", { replace: true });
        else if (res.status === 204) return { user_id: "undefined" };
        else if (res.status === 200) return res.json();
        else throw new Error(`Billing response ${res.status}`);
      })
      .then((data) => {
        setBillingData(data);
      })
      .catch((e) => {
        return;
      });
  };
  const getCustomerBeneficiary = () => {
    fetch(base_url + "user/beneficiaries", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 403) navigate("/logout", { replace: true });
        else if (res.status === 204) return [];
        if (res.status === 200) return res.json();
        else throw new Error();
      })
      .then((data) => {
        console.log(data);
        setCustomerBeneficiary(data);
      })
      .catch((e) => {
        console.log(e);
        return;
      });
  };

  const BillingUpdate = () => {
    fetch(base_url + "user/billing/update", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
        credentials: "include",
      },
      body: JSON.stringify(BillingData),
      credentials: "include",
    });
  };
  const CustomerUserUpdate = () => {
    fetch(base_url + "user/customeruser/update", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
        credentials: "include",
      },
      body: JSON.stringify(CustomerUser),
      credentials: "include",
    });
  };
  const getReferral = async () => {
    await fetch(base_url + "user/referral/data", {
      method: "POST",
      mode: "cors",
      body: ``,
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((res) => {
        setReferralData(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };
  const getUserValidationData = async () => {
    await fetch(base_url + "user/customer", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 403) navigate("/logout");
        else if (res.status === 200) return res.json();
        else throw new Error();
      })
      .then(async (data) => {
        if (data.user_type === "Partner" || data.user_type === "Influencer") {
          await fetch(base_url + "user/partner/info", {
            method: "POST",
            mode: "cors",
            body: ``,
            headers: {
              Accept: "application/json, text/plain, */*",
              "Content-type": "application/x-www-form-urlencoded",
              credentials: "include",
            },
            credentials: "include",
          })
            .then((res) => res.json())
            .then((res) => {
              setUserValidationData(res);
            })
            .catch((err) => {
              console.log(err);
            });
        } else {
          setUserValidationData({});
        }
      })
      .catch((e) => {
        console.log(e);
        navigate("/logout", { replace: true });
      });
  };
  const getMyPartner = async () => {
    await fetch(base_url + "user/mypartner", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
      body: ``,
    })
      .then((res) => (res.status === 200 ? res.json() : { partner: null }))
      .then((data) => setMyPartner(data?.partner || null))
      // Having no partner is the normal case, so a failure is not fatal here.
      .catch((err) => console.log(err));
  };
  const getPromotionData = async () => {
    await fetch(base_url + "user/referral/promotion/data", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 403) navigate("/logout");
        else if (res.status === 200) return res.json();
        else throw new Error();
      })
      .then(async (data) => {
        setPromotionData(data);
      })
      .catch((e) => {
        console.log(e);
        navigate("/logout", { replace: true });
      });
  };
  useEffect(() => {
    if (isUserPage) getUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [private_key, children]);
  /*
    Give a partner a sharing keypair if they have none.

    It is normally created at /partner/start, the once-only screen where a
    partner sets their first password -- so a partner who never went through it
    has none, and every sharing surface is dead for them with nothing on screen
    to say why. This backfills it on any sign-in and does nothing when one
    already exists.

    Keyed on CustomerUser rather than on mount: user_type is only known once
    that request lands. The ref keeps it to one attempt per session, since the
    effect re-runs whenever the record is refreshed.

    Wrapped under the account recovery key as well when this browser can recover
    it -- the same second way in /partner/start provides. Without one it is
    wrapped under the password alone: fully working, but a forgotten password
    would lose it.
  */
  const keypairChecked = useRef(false);
  useEffect(() => {
    if (!isUserPage || !private_key || !CustomerUser) return;
    if (keypairChecked.current) return;
    keypairChecked.current = true;
    (async () => {
      try {
        if (String(CustomerUser.user_type || "").toLowerCase() !== "partner")
          return;

        /*
          The account recovery key first, because the sharing keypair is wrapped
          under it. A partner's account is made by an admin and the code is
          issued at /partner/start, so a partner who never went through that
          screen has none -- and a forgotten password would cost them
          everything. Created here when missing, never replaced when present.
        */
        let recovery_key;
        const issued = await ensureRecoveryKey(private_key, CustomerUser);
        if (issued) {
          recovery_key = issued.recovery_key;
          setCustomerUser((current) => ({
            ...current,
            recovery_key_private_key: issued.recovery_key_private_key,
            private_key_recovery_key: issued.private_key_recovery_key,
          }));
        } else if (CustomerUser.recovery_key_private_key) {
          const recovered = await decryptText(
            private_key,
            CustomerUser.recovery_key_private_key
          );
          // decryptText answers with `""` when it cannot open it.
          if (recovered && recovered !== '""') recovery_key = recovered;
        }

        await ensurePartnerKeys(private_key, recovery_key);
      } catch (err) {
        // Sharing stays unavailable until the next sign-in; never a blocked one.
        console.log(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CustomerUser]);

  useEffect(() => {
    if (isUserPage) getBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);
  useEffect(() => {
    if (isUserPage) getMyPartner();
    /*
      Give this customer's partner a copy of their key, if they have a partner
      who can hold one. The server decides whether anything is needed; usually
      the answer is no and it costs one request.

      It has to happen somewhere the key exists -- nobody can wrap it on the
      customer's behalf -- and it has to happen somewhere the session is fully
      authenticated. Not after the password step: /user/* is guarded by
      mwUserExists, which requires req.session.otp and DESTROYS the session when
      it is missing. Called before OTP, this endpoint would silently kill the
      login it was riding on. Here it runs only on a /user page, which is only
      reachable once OTP is done, and it covers every route in -- OTP, recovery
      key, self recovery -- rather than just one.
    */
    if (isUserPage && private_key)
      publishKeyToPartner(private_key).catch((err) => console.log(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isUserPage) getReferral();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);
  useEffect(() => {
    if (isUserPage) getUserValidationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);
  useEffect(() => {
    if (isUserPage) getPromotionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isUserPage) getCustomerBeneficiary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);
  useEffect(() => {
    if (isUserPage) BillingUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BillingData]);
  useEffect(() => {
    if (isUserPage) CustomerUserUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CustomerUser]);
  // esto es nuevo
  useEffect(() => {
    if (isUserPage) getCustomerUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);
  useEffect(() => {
    if (!isUserPage) return;

    /*
      Date any asset that has changed before saving, so the copy on screen and
      the copy on the server carry the same stamp.

      Two passes, by design. The first finds the change, writes the stamp back
      through setUserData and stops; this effect runs again on the result, and
      that pass saves. stampUpdatedAssets returns null the second time -- the
      date it would write is already there -- which is what ends it rather
      than looping. One save, not two.

      Nothing is stamped until an account has loaded and established a
      baseline: before that there is nothing to compare against, and every
      asset would look new.
    */
    if (UserData?.user_id) {
      if (lastSyncedAssets.current) {
        const stamped = stampUpdatedAssets(
          UserData.assets,
          lastSyncedAssets.current
        );
        if (stamped) {
          setUserData({ ...UserData, assets: stamped });
          return;
        }
      }
      lastSyncedAssets.current = Array.isArray(UserData.assets)
        ? UserData.assets
        : [];
    }

    UserDataUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UserData, upda, private_key]);
  useEffect(() => {
    if (window.sessionStorage.getItem("ostreon_forcePWD")) {
      console.log("ha entrado");
      navigate("/user/changepwd", { replace: true });
    }
  }, []);
  const updateProducts = () => {
    fetchStripeProducts()
      .then((data) => {
        console.log(data);
        setProducts(data.products);
        setCurrency(data.currency);
      })
      .catch((e) => {
        console.log(e);
      });
  };
  useEffect(() => {
    updateProducts();
  }, []);

  return (
    <DataContext.Provider
      value={{
        UserData,
        private_key,
        CustomerUser,
        edit,
        setUserData,
        setCustomerUser,
        Update,
        email,
        setEmail,
        password,
        setPassword,
        BillingData,
        setBillingData,
        getBillingData,
        getUserData,
        CustomerBeneficiary,
        getCustomerBeneficiary,
        setCustomerBeneficiary,
        MyAccount,
        setMyAccount,
        getCustomerUser,
        isFreezed,
        products,
        updateProducts,
        setFreezed,
        currency,
        userValidationData,
        myPartner,
        getMyPartner,
        getUserValidationData,
        setUserValidationData,
        referralData,
        getReferral,
        setReferralData,
        blink,
        pushBlink,
        promotionData,
        getPromotionData,
      }}
    >
      {private_key &&
      UserData &&
      CustomerUser &&
      CustomerBeneficiary &&
      BillingData &&
      referralData &&
      userValidationData ? (
        children
      ) : (
        <Loading text={error ? error : dublicatedcontent?.loading_ostreon} />
      )}
    </DataContext.Provider>
  );
};

export const UserData = () => {
  return useContext(DataContext);
};
